import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCamera } from 'react-icons/fi';
import promiseRequest from '@/app/utils/promiseToast';
import imgTia from '@/app/components/TIA/img/tia-avatar.png';
import ocr_success_img from '@/app/components/ImgSuccessModal/img/ocr_success_img.png';
import { SimpleModal } from '@/app/components/Modal';
import { Loader } from '@/components/custom/loader';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { uploadFileFromPresignedUrl } from '@/app/utils/aws-sdk';
import ActionCableConsumer from '@/app/utils/actionCableConsumer';
import { useSession } from '@/app/hooks/useSession';
import { DashboardContent } from '@/layouts/dashboard';
import { Button, ButtonIcon } from '@/components/ui/button';
import { EssaySendBody, EssaySendCard, EssaySendCardHeader, EssaySendCardTitle, EssaySendHeader, EssaySendCardPlaceholder, EssaySendCardTextarea } from '@/components/custom/essay';
import { InputFile } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AvatarWrapper } from '@/components/ui/avatar';

const MAX_DIM = 10000;

function processImage(file) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => {
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name, { type: file.type }));
        }, file.type);
      } else {
        URL.revokeObjectURL(img.src);
        resolve(file);
      }
    };
    img.src = URL.createObjectURL(file);
  });
}

export function EssaySendContent({ essayId, studentId }) {
  const navigate = useNavigate();
  const [imageLabel, setImageLabel] = useState();
  const [openLoaderModal, setOpenLoaderModal] = useState(false);
  const [openImgModal, setOpenImgModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [webhookNotification, setWebhookNotification] = useState(null);
  const textareaRef = useRef(null);
  const { promiseToast, isSubmitting } = promiseRequest();
  const client = useClient();
  const [essayText, setEssayText] = useState('');
  const [essayStudentId, setEssayStudentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submissionsNumber, setSubmissionsNumber] = useState();
  const fileInputRef = useRef(null);
  const [sendingByTeacherId, setSendingByTeacherId] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  // const [showDelayMessage, setShowDelayMessage] = useState(false);
  // const timerRef = useRef(null);

  const { session } = useSession();

  const getEssayStatus = async () => {
    setLoading(true);
    const url = studentId ? `/essays/${essayId}?show_for_student_id=${studentId}` : `/essays/${essayId}`;
    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error('Erro ao buscar status da redação');
    } else {
      const status = data['essay-student'].status;
      const essayStudentId = data['essay-student'].id;
      setSubmissionsNumber(data['essay-student']?.['submission_order']);
      setEssayStudentId(data['essay-student']?.id);

      if (status === 'ocr_review') {
        const { data, error } = await client.fetch(`/essays/${essayId}/essay_students/${essayStudentId}`);
        if (error) {
          toast.error('Erro ao buscar status da redação');
        } else {
          setEssayText(data?.body);
          setImageLabel(data?.['essay_image']?.url);
        }
      }
    }
    setLoading(false);
  };

  const transcribeEssay = async e => {
    setOpenLoaderModal(true);
    let file = e.target.files[0];
    if (!file) return;
    try {
      file = await processImage(file);
    } catch (err) {
      console.warn('resize falhou, usando original', err);
    }
    setSendingByTeacherId(session?.user?.id);

    // Build form data with conditional user_id if studentId exists
    const formData = {
      filename: file.name,
      content_type: file.type,
      ...(studentId && { user_id: studentId })
    };

    try {
      // Step 1: Create essay student record
      const postRes = await client.mutate(`/essays/${essayId}/essay_students`, formData);
      const uploadUrl = decodeURI(postRes.data['upload-url']);
      const essayStudentId = postRes.data.id;

      // Step 2: Upload file using the pre-signed URL
      const uploadedFileUrl = await uploadFileFromPresignedUrl(uploadUrl, file);
      const fileName = uploadedFileUrl.split('/').pop();

      // Step 3: Update essay student record with the uploaded image name
      const patchRes = await client.mutate(`/essays/${essayId}/essay_students/${essayStudentId}`, { essay_image_name: fileName }, { method: 'PATCH' });

      if (patchRes.error) {
        toast.error(patchRes.error.message);
        return;
      }

      const patchData = patchRes.data;

      if (!patchData.body || patchData.body.length < 100) {
        setOpenErrorModal(true);
        setEssayText(null);
      } else {
        setEssayText(patchData.body);
        setEssayStudentId(patchData.id);
        setImageLabel(patchData.essay_image.url);
        setOpenSuccessModal(true);
      }
    } catch (err) {
      toast.error('Houve um problema ao enviar a imagem da redação.');
      console.log(err);
    } finally {
      setOpenLoaderModal(false);
    }
  };

  const sendEssay = async () => {
    promiseToast({
      url: `/essays/${essayId}/essay_students/${essayStudentId}/correction`,
      request: { essay_student_content_attributes: { body: essayText } },
      successText: 'Redação enviada com sucesso',
      errorText: 'Erro ao enviar redação',
      onSuccess: () => navigate(`/redacao/${studentId ? 'acompanhamento' : 'sobre'}/${essayId}`)
    });
  };

  useEffect(() => {
    getEssayStatus();
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = essayText;
    }
  }, [essayText]);

  const handleReceivedNotification = data => {
    setWebhookNotification(data);
  };

  useEffect(() => {
    if (!webhookNotification) return;

    if (webhookNotification?.id && sendingByTeacherId !== session?.user?.id && parseInt(studentId) === webhookNotification?.user_id && webhookNotification?.status === 'sending_by_teacher') {
      toast.error('Outro professor já enviou essa redação.');
      navigate(`/redacao/acompanhamento/${essayId}`);
    }
    // if (webhookNotification?.id && parseInt(studentId) === webhookNotification?.user_id && webhookNotification?.status === 'ocr_completed') {
    //   setEssayText(webhookNotification.text);
    //   setEssayStudentId(webhookNotification.id);
    //   setImageLabel(webhookNotification.essay_image_url);
    //   setOpenLoaderModal(false);
    //   setOpenSuccessModal(true);
    // }
    // if (webhookNotification?.id && parseInt(studentId) === webhookNotification?.user_id && webhookNotification?.status === 'ocr_error') {
    //   toast.error(webhookNotification.message);
    //   setOpenLoaderModal(false);
    // }
  }, [webhookNotification]);

  // useEffect(() => {
  //   if (openLoaderModal) {
  //     timerRef.current = setTimeout(() => {
  //       setShowDelayMessage(true);
  //     }, 30000);
  //   }
  //   return () => {
  //     if (timerRef.current) {
  //       clearTimeout(timerRef.current);
  //     }
  //   };
  // }, [openLoaderModal]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {essayId && studentId && (
        <ActionCableConsumer
          channel="EssayStudentChannel"
          setObjectChannel={handleReceivedNotification}
          channelKey={'essay_id'}
          channelId={essayId}
        />
      )}

      <DashboardContent
        viewMode="create"
        className="!pt-12"
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <EssaySendHeader>{imageLabel && <Button onClick={sendEssay}>Enviar para revisão</Button>}</EssaySendHeader>

            <EssaySendBody>
              {imageLabel ? (
                <EssaySendCard>
                  <EssaySendCardHeader>
                    <EssaySendCardTitle>Foto da sua redação</EssaySendCardTitle>

                    <InputFile
                      variant="button"
                      buttonVariant="link"
                      buttonClassName="p-0 h-5 font-light text-neutral-dark"
                      label="Enviar outra foto"
                      ref={fileInputRef}
                      type="file"
                      name="image"
                      id="image"
                      accept=".jpg, .jpeg, .png, .heic, .heif"
                      onChange={transcribeEssay}
                    />
                  </EssaySendCardHeader>

                  <div className="flex items-center gap-2">
                    <img
                      src={imageLabel}
                      alt="imagem da redação"
                      className="cursor-pointer"
                      onClick={() => setOpenImgModal(true)}
                    />
                  </div>
                </EssaySendCard>
              ) : (
                <EssaySendCard>
                  <EssaySendCardPlaceholder>
                    <div className="text-center">
                      <label className="flex cursor-pointer flex-col gap-2 font-bold">
                        <EssaySendCardTitle>Clique no botão abaixo para enviar sua redação</EssaySendCardTitle>
                        <p className="mb-6 font-light">A foto deve estar jpg, jpeg, png, heic ou heif </p>
                        <input
                          className="hidden"
                          type="file"
                          name="image"
                          id="image"
                          accept=".jpg, .jpeg, .png, .heic, .heif"
                          onChange={transcribeEssay}
                          disabled={imageLabel}
                        />
                        <Button
                          wide
                          disabled={imageLabel}
                          asChild
                        >
                          <span>
                            <ButtonIcon>
                              <FiCamera />
                            </ButtonIcon>
                            Enviar foto da redação
                          </span>
                        </Button>
                      </label>
                    </div>
                  </EssaySendCardPlaceholder>
                </EssaySendCard>
              )}

              {essayText && (
                <EssaySendCard>
                  <EssaySendCardHeader>
                    <EssaySendCardTitle>Revise o texto digitalizado</EssaySendCardTitle>
                  </EssaySendCardHeader>

                  <EssaySendCardTextarea>
                    <textarea
                      ref={textareaRef}
                      onBlur={() => setEssayText(textareaRef.current?.value)}
                    >
                      {essayText}
                    </textarea>
                  </EssaySendCardTextarea>
                </EssaySendCard>
              )}
            </EssaySendBody>
          </>
        )}
      </DashboardContent>

      <Dialog
        open={openErrorModal}
        onOpenChange={() => setOpenErrorModal(false)}
      >
        <DialogContent size="sm">
          <div style={{ textAlign: 'center' }}>
            <DialogTitle className="sr-only">Arquivo inválido</DialogTitle>
            <p>A imagem enviada não parecer ser de uma redação. Envie uma imagem válida para seguirmos com a correção</p>
            <label className="form__custom-file-input u-d-inline-block">
              <input
                type="file"
                name="image"
                id="image"
                accept=".jpg, .jpeg, .png, .heic, .heif"
                onChange={transcribeEssay}
              />
              <span className="btn btn--primary btn--wide">Enviar outra foto</span>
            </label>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openSuccessModal}
        onOpenChange={() => setOpenSuccessModal(false)}
      >
        <DialogContent size="lg">
          <DialogTitle className="sr-only">Revise</DialogTitle>

          <div
            style={{
              display: isMobile ? 'block' : 'flex',
              textAlign: isMobile ? 'center' : 'left',
              alignItems: 'center',
              gap: '32px',
              padding: '32px',
              maxWidth: '1000px'
            }}
          >
            {/* Ilustração */}
            <div style={{ flex: '1', display: 'flex', justifyContent: 'center' }}>
              <img
                src={ocr_success_img} // substitua com o caminho correto
                alt="OCR concluído"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>

            {/* Texto + Botão */}
            <div style={{ flex: '1' }}>
              <h2 style={{ color: '#5A2D82', fontSize: '24px', fontWeight: 700, marginBottom: '16px' }}>Redação digitalizada com sucesso!</h2>
              <p style={{ fontSize: '16px', marginBottom: '32px' }}>
                Agora revise o resultado da digitalização e clique em <strong>Enviar para revisão</strong>
              </p>
              <Button
                style={{ width: '160px' }}
                onClick={() => setOpenSuccessModal(false)}
              >
                Entendi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openImgModal}
        onOpenChange={() => setOpenImgModal(false)}
      >
        <DialogContent size="lg">
          <DialogTitle className="sr-only">Prova digitalizada</DialogTitle>
          <img
            src={imageLabel}
            alt="prova digitalizada"
          />
        </DialogContent>
      </Dialog>

      <SimpleModal
        show={openLoaderModal}
        onClose={() => setOpenLoaderModal(false)}
        hideCloseButton
        closeOnOverlayClick={false}
      >
        <div className="flex items-start gap-3">
          <AvatarWrapper
            size="md"
            src={imgTia}
            text="T.I.A"
          />
          <div className="grow">
            <span className="font-bold text-primary">Aguarde enquanto digitalizamos sua redação</span>
            <p>Não feche essa tela</p>
            {/* <p className="text-center">Não feche essa tela</p>
            {showDelayMessage && (
              <p className="text-center">
                A fila está um pouco grande no momento. <br /> Segure firme, estamos quase lá...
              </p>
            )} */}
            <Loader />
          </div>
        </div>
      </SimpleModal>
    </>
  );
}
